import {Box} from '@mui/material'
import type {ChartData, ChartOptions, Plugin} from 'chart.js'
import {Chart} from 'chart.js/auto'
import 'chartjs-adapter-moment'
import {useRef, useEffect, MutableRefObject} from 'react'

import {
  calcSpanGaps,
  computeLineColor
} from '../../../plantMonitoring/shared/containers/charts/chartHelpers'

type BaseChartProps = {
  data: ChartData & {bucketSize?: string}
  options: ChartOptions
  extChartRef?: MutableRefObject<Chart | null>
  plugins?: Plugin[]
}

// spacing between legend and chart, remove once available in config
const plugin: Plugin = {
  id: 'legendSpacing',
  beforeInit(chart) {
    const legend = chart.legend
    if (!legend) return

    const originalFit = legend['fit'].bind(legend)
    legend.fit = function fit(this: typeof legend) {
      originalFit()
      this.height += 15
    }
  }
}

export const BaseChart = ({data, options, plugins, extChartRef}: BaseChartProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const intRef = useRef<Chart | null>(null)
  const chartRef = extChartRef ? extChartRef : intRef

  const renderChart = () => {
    if (!canvasRef.current) return

    chartRef.current = new Chart(canvasRef.current, {
      type: 'line',
      options,
      plugins: [plugin, ...(plugins ?? [])],
      data
    })
  }

  const destroyChart = () => {
    if (chartRef.current) {
      chartRef.current.destroy()
      chartRef.current = null
    }
  }

  useEffect(() => {
    if (chartRef.current && data) {
      // update datasets individually to prevent resetting filters and triggering redraw on new data fetch
      if (data.datasets.length === chartRef.current.data.datasets.length) {
        chartRef.current.data.datasets.forEach((dataset) => {
          dataset.data = data.datasets.find((d) => d.label === dataset.label)?.data ?? []

          if ('spanGaps' in dataset) {
            dataset.spanGaps = calcSpanGaps(data.bucketSize)
          }

          if ('segment' in dataset && dataset.segment) {
            const mainData = data.datasets.find((d) => d.label === 'Signal')?.data
            const minData = data.datasets.find((d) => d.label === 'modelMin')?.data
            const maxData = data.datasets.find((d) => d.label === 'modelMax')?.data

            if (mainData) {
              dataset.spanGaps = calcSpanGaps(data.bucketSize)
              dataset.segment = {
                ...computeLineColor(
                  mainData as unknown as number[],
                  minData as unknown as (number | null)[],
                  maxData as unknown as (number | null)[],
                  dataset.spanGaps
                )
              }
            }
          }
        })
      } else {
        // if the number of datasets has changed, we have to replace them at once
        chartRef.current.data.datasets = data.datasets
      }
      chartRef.current.data.labels = data.labels
      chartRef.current.update()
    }
  }, [data, chartRef])

  // Order of useEffect matters, options should be updated after data
  useEffect(() => {
    if (chartRef.current && options) {
      chartRef.current.options = options
      chartRef.current.update()
    }
  }, [options, chartRef])

  useEffect(() => {
    renderChart()

    return () => destroyChart()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Box sx={{height: '400px', position: 'relative'}}>
      <canvas ref={canvasRef} />
    </Box>
  )
}
